import React, { useMemo, useState } from 'react';
import AppLayout from '../../components/AppLayout';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import useTableList from '../../utils/useTableList';
import { YES_NO_OPTIONS } from '../../constants/listOptions';
import ConfirmDialog from '../../components/ConfirmDialog';
import Drawer from '../../components/Drawer';
import useEditRoute from '../../utils/useEditRoute';
import usePageTitle from '../../utils/usePageTitle';
import { clientTypesService } from '../../services/salesService';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

function ClientTypesList() {
    usePageTitle('Tipos de cliente');
    const { toast, toastApiError } = useToast();
    const { can } = useAuth();
    const canClients = can('Clients');

    const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingType, setEditingType] = useState(null);
    const [formLoading, setFormLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
    });

    const list = useTableList({
        storageKey: 'client-types',
        fetcher: (params) => clientTypesService.list(params),
        errorMessage: 'Não foi possível carregar os tipos de cliente',
    });

    const columns = useMemo(() => ([
        {
            key: 'name',
            label: 'Nome',
            className: 'font-medium',
        },
    ]), []);

    const filters = [
        { key: 'name', label: 'Nome', type: 'text' },
        { key: 'has_clients', label: 'Possui clientes', type: 'select', options: YES_NO_OPTIONS },
    ];

    const openNew = () => {
        setEditingType(null);
        setFormData({ name: '' });
        setDrawerOpen(true);
    };

    const openEdit = async (row) => {
        setFormLoading(true);
        try {
            const response = await clientTypesService.get(row.id);
            const data = response.data?.data || response.data || row;
            setEditingType(data);
            setFormData({ name: data.name || '' });
            setDrawerOpen(true);
        } catch (e) {
            console.error('Error loading client type:', e);
            toastApiError(e, { title: 'Erro ao carregar', fallback: 'Não foi possível carregar o tipo de cliente' });
        } finally {
            setFormLoading(false);
        }
    };

    const handleDeleteClick = (row) => {
        setDeleteDialog({ open: true, id: row.id });
    };

    const handleDeleteConfirm = async () => {
        try {
            await clientTypesService.delete(deleteDialog.id);
            setDeleteDialog({ open: false, id: null });
            list.reload();
            toast({
                title: 'Tipo de cliente excluído',
                description: 'O registro foi excluído com sucesso',
                variant: 'success',
            });
        } catch (e) {
            console.error('Error deleting client type:', e);
            toastApiError(e, { title: 'Erro ao excluir', fallback: 'Não foi possível excluir o tipo de cliente' });
        }
    };

    const handleFormChange = (e) => {
        setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSave = async () => {
        if (formLoading) return;
        setFormLoading(true);
        try {
            const payload = { name: formData.name };

            if (editingType?.id) {
                const response = await clientTypesService.update(editingType.id, payload);
                const saved = response.data?.data || response.data;
                if (saved) setEditingType(saved);
                toast({
                    title: 'Tipo de cliente salvo',
                    description: 'Alterações salvas com sucesso',
                    variant: 'success',
                });
            } else {
                const response = await clientTypesService.create(payload);
                const saved = response.data?.data || response.data;
                if (saved) setEditingType(saved);
                toast({
                    title: 'Tipo de cliente criado',
                    description: 'Cadastro realizado com sucesso',
                    variant: 'success',
                });
            }

            list.reload();
        } catch (e) {
            console.error('Error saving client type:', e);
            toastApiError(e, { title: 'Erro ao salvar', fallback: 'Não foi possível salvar o tipo de cliente' });
        } finally {
            setFormLoading(false);
        }
    };

    useEditRoute({
        basePath: '/settings/client-types',
        ready: list.loaded,
        drawerOpen: drawerOpen,
        open: id => openEdit({ id }),
    });

    return (
        <AppLayout>
            <div className="space-y-6">
                <PageHeader
                    title="Tipos de cliente"
                    breadcrumbs={[
                        { label: 'Dashboard', path: '/dashboard' },
                        { label: 'Cadastros' },
                    ]}
                    actions={[
                        ...(canClients ? [{
                            label: 'Novo tipo de cliente',
                            icon: 'new',
                            onClick: openNew,
                        }] : []),
                    ]}
                />

                <DataTable
                    list={list}
                    filters={filters}
                    searchPlaceholder="Buscar por nome..."
                    columns={columns}
                    editTo={canClients ? (row) => `/settings/client-types/${row.id}` : undefined}
                    onEdit={canClients ? openEdit : undefined}
                    onDelete={canClients ? handleDeleteClick : undefined}
                    emptyMessage="Nenhum tipo de cliente cadastrado"
                />
            </div>

            <ConfirmDialog
                open={deleteDialog.open}
                title="Excluir tipo de cliente"
                message="Tem certeza que deseja excluir este tipo de cliente? Esta ação não pode ser desfeita."
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteDialog({ open: false, id: null })}
            />

            <Drawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                title={editingType?.id ? 'Editar tipo de cliente' : 'Novo tipo de cliente'}
                size="md"
                footer={
                    <div className="flex justify-end gap-3">
                        <Button type="button" variant="outline" onClick={() => setDrawerOpen(false)}>
                            Cancelar
                        </Button>
                        <Button type="button" onClick={handleSave} disabled={formLoading || !canClients}>
                            {formLoading ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </div>
                }
            >
                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nome *
                        </label>
                        <Input
                            name="name"
                            value={formData.name}
                            onChange={handleFormChange}
                            required
                        />
                    </div>
                </div>
            </Drawer>
        </AppLayout>
    );
}

export default ClientTypesList;
